/**
 * ComplianceBadge Component
 * Interactive badge showing certificate compliance status with tooltip details
 */

import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, AlertCircle, XCircle } from 'lucide-react';

// Group certificates by status
const groupCertificates = (certificates) => {
  const now = new Date();

  const expired = [];
  const expiring = []; // 0-90 days
  const compliant = []; // > 90 days

  certificates.forEach(cert => {
    const expiryDate = cert.expiry_date || cert.expiry;
    if (!expiryDate) {
      compliant.push({ ...cert, daysUntil: null }); // No expiry = assume valid
      return;
    }

    const expiry = new Date(expiryDate);
    const daysUntil = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      expired.push({ ...cert, daysUntil, expiryDate });
    } else if (daysUntil < 30) {
      expiring.push({ ...cert, daysUntil, urgency: 'high', expiryDate });
    } else if (daysUntil < 90) {
      expiring.push({ ...cert, daysUntil, urgency: 'medium', expiryDate });
    } else {
      compliant.push({ ...cert, daysUntil, expiryDate });
    }
  });

  return { expired, expiring, compliant };
};

// Determine overall status
const getOverallStatus = (grouped) => {
  if (grouped.expired.length > 0) return 'expired';
  if (grouped.expiring.length > 0) return 'expiring';
  return 'compliant';
};

// Status badge configuration
const StatusBadge = ({ status, count }) => {
  const config = {
    expired: {
      icon: XCircle,
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      label: 'Expired'
    },
    expiring: {
      icon: AlertCircle,
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-200',
      label: 'Expiring'
    },
    compliant: {
      icon: CheckCircle,
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      label: 'Certs Valid'
    },
    none: {
      icon: AlertCircle,
      bg: 'bg-gray-100',
      text: 'text-gray-600',
      border: 'border-gray-200',
      label: 'No Certs'
    }
  };

  const { icon: Icon, bg, text, border, label } = config[status] || config.none;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${bg} ${text} ${border} text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      {count > 0 && (
        <span className="ml-0.5 opacity-70">({count})</span>
      )}
    </div>
  );
};

// Individual certificate item in tooltip
const CertificateItem = ({ cert }) => {
  const formatDate = (date) => {
    if (!date) return 'No expiry';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysText = (days) => {
    if (days === null || days === undefined) return '';
    if (days < 0) return `Expired ${Math.abs(days)} days ago`;
    if (days === 0) return 'Expires today';
    if (days === 1) return 'Expires tomorrow';
    return `Expires in ${days} days`;
  };

  const certName = cert.type || cert.name || cert.certificateType || 'Certificate';

  return (
    <div className="flex justify-between items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{certName}</div>
        <div className="text-gray-400 text-[10px]">
          {getDaysText(cert.daysUntil)}
        </div>
      </div>
      <div className="text-gray-300 text-[10px] whitespace-nowrap">
        {formatDate(cert.expiryDate)}
      </div>
    </div>
  );
};

// Certificate tooltip content
const CertificateTooltip = ({ grouped }) => {
  return (
    <div className="w-72 bg-gray-900 text-white text-xs rounded-lg shadow-xl overflow-hidden">
      {/* Expired Certificates */}
      {grouped.expired.length > 0 && (
        <div className="border-b border-gray-700">
          <div className="bg-red-900 px-3 py-2 font-semibold flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            Expired ({grouped.expired.length})
          </div>
          <div className="px-3 py-2 space-y-1.5">
            {grouped.expired.map((cert, idx) => (
              <CertificateItem key={idx} cert={cert} />
            ))}
          </div>
        </div>
      )}

      {/* Expiring Soon */}
      {grouped.expiring.length > 0 && (
        <div className="border-b border-gray-700">
          <div className="bg-amber-900 px-3 py-2 font-semibold flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Expiring Soon ({grouped.expiring.length})
          </div>
          <div className="px-3 py-2 space-y-1.5">
            {grouped.expiring.map((cert, idx) => (
              <CertificateItem key={idx} cert={cert} />
            ))}
          </div>
        </div>
      )}

      {/* Compliant */}
      {grouped.compliant.length > 0 && (
        <div>
          <div className="bg-green-900 px-3 py-2 font-semibold flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Certs Valid ({grouped.compliant.length})
          </div>
          <div className="px-3 py-2 space-y-1.5 max-h-40 overflow-y-auto">
            {grouped.compliant.map((cert, idx) => (
              <CertificateItem key={idx} cert={cert} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main ComplianceBadge component
const ComplianceBadge = ({ certificates = [] }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState('left');
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);

  // Group certificates by status
  const grouped = groupCertificates(certificates);

  // Determine overall status
  const status = certificates.length === 0 ? 'none' : getOverallStatus(grouped);

  // Handle tooltip position to prevent overflow
  useEffect(() => {
    if (showTooltip && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tooltipWidth = 288; // w-72 = 18rem = 288px

      if (rect.left + tooltipWidth > window.innerWidth) {
        setTooltipPosition('right');
      } else {
        setTooltipPosition('left');
      }
    }
  }, [showTooltip]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={(e) => {
        e.stopPropagation(); // Prevent row click
        setShowTooltip(!showTooltip);
      }}
    >
      {/* Badge */}
      <StatusBadge status={status} count={certificates.length} />

      {/* Tooltip */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 bottom-full mb-2 ${tooltipPosition === 'left' ? 'left-0' : 'right-0'}`}
        >
          {certificates.length === 0 ? (
            <div className="w-48 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
              No certificates recorded
            </div>
          ) : (
            <CertificateTooltip grouped={grouped} />
          )}
        </div>
      )}
    </div>
  );
};

export default ComplianceBadge;
